/**
 * JSON Schema에 포함된 titleI18n, descriptionI18n을 현재 언어에 맞게
 * title, description으로 변환하여 RJSF(Json Schema Form)에 전달할 스키마를 만든다.
 *
 * - 루트는 객체만 지원한다. 배열이거나 null/undefined면 빈 객체를 반환한다.
 * - 순회하여 i18n을 적용하는 키: properties, oneOf, anyOf, allOf, items, dependencies, definitions, $defs
 * - 그 외 키(enum, default 등)는 그대로 전달되며, 해당 하위 노드의 titleI18n/descriptionI18n은 치환되지 않는다.
 */

export type I18nLocale = 'ko' | 'en';

/** i18n 적용 시 최대 재귀 깊이. 초과 시 해당 노드는 변환하지 않고 반환한다. */
const MAX_SCHEMA_DEPTH = 32;

/** 문자열 스키마 허용 최대 길이(바이트). 초과 시 파싱하지 않고 빈 객체를 반환한다. */
const MAX_SCHEMA_STRING_LENGTH = 512 * 1024;

/**
 * i18next 등에서 오는 언어 코드를 스키마 locale('ko' | 'en')로 정규화한다.
 * 예: 'ko-KR' → 'ko', 'en-US' → 'en'
 */
export const normalizeLocale = (lng: string): I18nLocale => (lng.startsWith('ko') ? 'ko' : 'en');

interface SchemaNodeWithI18n {
  title?: string;
  titleI18n?: Partial<Record<I18nLocale, string>>;
  description?: string;
  descriptionI18n?: Partial<Record<I18nLocale, string>>;
  properties?: Record<string, SchemaNodeWithI18n>;
  oneOf?: SchemaNodeWithI18n[];
  anyOf?: SchemaNodeWithI18n[];
  allOf?: SchemaNodeWithI18n[];
  items?: SchemaNodeWithI18n | SchemaNodeWithI18n[];
  dependencies?: Record<string, SchemaNodeWithI18n | SchemaNodeWithI18n[]>;
  definitions?: Record<string, SchemaNodeWithI18n>;
  $defs?: Record<string, SchemaNodeWithI18n>;
  [key: string]: unknown;
}

const SCHEMA_ARRAY_KEYS = ['oneOf', 'anyOf', 'allOf'] as const;

/**
 * 단일 스키마 노드에서 i18n 필드를 적용한 새 노드를 반환한다.
 * titleI18n[locale] 또는 descriptionI18n[locale]이 있으면 해당 값으로 치환하고,
 * 없으면 원본 title/description을 그대로 둔다. (스키마에는 항상 title/description이 있음)
 * titleI18n/descriptionI18n 키는 제거한다.
 * 깊이가 MAX_SCHEMA_DEPTH를 초과하면 순환 방지를 위해 더 이상 재귀하지 않는다.
 */
function resolveNodeI18n(
  node: SchemaNodeWithI18n,
  locale: I18nLocale,
  depth: number,
): SchemaNodeWithI18n {
  if (node === null || typeof node !== 'object') {
    return node;
  }

  if (depth > MAX_SCHEMA_DEPTH) {
    return node;
  }

  const nextDepth = depth + 1;
  const { titleI18n, descriptionI18n, ...rest } = node;

  const resolved: SchemaNodeWithI18n = { ...rest };

  if (titleI18n && typeof titleI18n === 'object' && titleI18n[locale] != null) {
    resolved.title = titleI18n[locale] as string;
  }
  if (descriptionI18n && typeof descriptionI18n === 'object' && descriptionI18n[locale] != null) {
    resolved.description = descriptionI18n[locale] as string;
  }

  if (resolved.properties && typeof resolved.properties === 'object') {
    resolved.properties = Object.fromEntries(
      Object.entries(resolved.properties).map(([k, v]) => [
        k,
        resolveNodeI18n(v as SchemaNodeWithI18n, locale, nextDepth),
      ]),
    );
  }

  for (const key of SCHEMA_ARRAY_KEYS) {
    if (Array.isArray(resolved[key])) {
      (resolved as Record<string, unknown>)[key] = (resolved[key] as SchemaNodeWithI18n[]).map(
        item => resolveNodeI18n(item, locale, nextDepth),
      );
    }
  }

  if (resolved.items) {
    resolved.items = Array.isArray(resolved.items)
      ? (resolved.items as SchemaNodeWithI18n[]).map(item => resolveNodeI18n(item, locale, nextDepth))
      : resolveNodeI18n(resolved.items as SchemaNodeWithI18n, locale, nextDepth);
  }

  if (resolved.dependencies && typeof resolved.dependencies === 'object') {
    resolved.dependencies = Object.fromEntries(
      Object.entries(resolved.dependencies).map(([key, value]) => {
        const val = value as SchemaNodeWithI18n | SchemaNodeWithI18n[];
        return [
          key,
          Array.isArray(val)
            ? val.map(v => resolveNodeI18n(v, locale, nextDepth))
            : resolveNodeI18n(val, locale, nextDepth),
        ];
      }),
    );
  }

  if (resolved.definitions && typeof resolved.definitions === 'object') {
    resolved.definitions = Object.fromEntries(
      Object.entries(resolved.definitions).map(([k, v]) => [
        k,
        resolveNodeI18n(v as SchemaNodeWithI18n, locale, nextDepth),
      ]),
    );
  }

  if (resolved.$defs && typeof resolved.$defs === 'object') {
    resolved.$defs = Object.fromEntries(
      Object.entries(resolved.$defs).map(([k, v]) => [
        k,
        resolveNodeI18n(v as SchemaNodeWithI18n, locale, nextDepth),
      ]),
    );
  }

  return resolved;
}

/**
 * connectionInfoSchema / addUserSchema 등 JSON Schema(문자열 또는 객체)를
 * 현재 locale에 맞게 변환한다. RJSF Form의 schema prop에 그대로 넘길 수 있는 객체를 반환한다.
 *
 * @param schema - connectionInfoSchema (문자열이면 JSON.parse 후 처리). 루트는 객체만 지원.
 * @param locale - 'ko' | 'en'
 * @returns 변환된 스키마 객체. 파싱 실패·길이 초과·null/배열 입력 시 빈 객체.
 */
export const resolveConnectionInfoSchemaI18n = (
  schema: string | Record<string, unknown> | null | undefined,
  locale: I18nLocale,
): Record<string, unknown> => {
  if (schema == null) {
    return {};
  }

  let parsed: SchemaNodeWithI18n;
  if (typeof schema === 'string') {
    if (schema.length > MAX_SCHEMA_STRING_LENGTH) {
      if (import.meta.env.DEV) {
        console.warn(
          '[JsonSchemaUtil] Schema string length exceeds limit:',
          schema.length,
          'max:',
          MAX_SCHEMA_STRING_LENGTH,
        );
      }
      return {};
    }
    try {
      parsed = JSON.parse(schema) as SchemaNodeWithI18n;
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn('[JsonSchemaUtil] Failed to parse schema string:', e);
      }
      return {};
    }
  } else if (typeof schema === 'object' && !Array.isArray(schema)) {
    parsed = schema as SchemaNodeWithI18n;
  } else {
    return {};
  }

  return resolveNodeI18n(parsed, locale, 0) as Record<string, unknown>;
};
